import React from 'react';
import { FaUser, FaBuilding, FaCalendar } from 'react-icons/fa';

const CoverLetterPreview = ({ data, template }) => {
  const { personalInfo, jobInfo, content } = data;

  const getTemplateStyles = () => {
    switch (template) {
      case 'creative':
        return {
          container: 'bg-gradient-to-br from-blue-900/50 to-purple-900/50',
          header: 'border-b-2 border-blue-500/30',
          title: 'bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent',
          section: 'bg-gray-900/30',
          text: 'text-gray-300',
        };
      case 'minimal':
        return {
          container: 'bg-gray-900/50',
          header: 'border-b border-white/10',
          title: 'text-white',
          section: 'bg-gray-900/30',
          text: 'text-gray-300',
        };
      case 'traditional':
        return {
          container: 'bg-gradient-to-br from-gray-900/50 to-gray-800/50',
          header: 'border-b-2 border-gray-600/30',
          title: 'text-white font-serif',
          section: 'bg-gray-900/30',
          text: 'text-gray-300',
        };
      default: // modern
        return {
          container: 'bg-gradient-to-br from-gray-900/50 to-blue-900/50',
          header: 'border-b-2 border-blue-500/30',
          title: 'bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent',
          section: 'bg-gray-900/30',
          text: 'text-gray-300',
        };
    }
  };

  const styles = getTemplateStyles();

  return (
    <div className={`${styles.container} p-8 rounded-xl backdrop-blur-sm min-h-[1056px] w-full max-w-[816px] mx-auto transition-all duration-300`} style={{ aspectRatio: '1/1.4142' }}>
      {/* Header with Personal Info */}
      <div className={`${styles.header} pb-6 mb-8`}>
        <h1 className={`${styles.title} text-4xl font-bold mb-4`}>{personalInfo.fullName || 'Your Name'}</h1>
        <div className="flex flex-wrap gap-4 text-sm">
          {personalInfo.address && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800/50 text-gray-300">
              <FaUser className="text-blue-400" />
              <span>{personalInfo.address}</span>
            </div>
          )}
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800/50 text-gray-300">
            <FaCalendar className="text-purple-400" />
            <span>{new Date(personalInfo.date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
        </div>
      </div>

      {/* Recipient Info */}
      {(jobInfo.hiringManager || jobInfo.companyName || jobInfo.companyAddress) && (
        <div className={`${styles.section} mb-8 p-6 rounded-xl space-y-2`}>
          {jobInfo.hiringManager && (
            <div className="flex items-center gap-2 text-gray-300">
              <FaUser className="text-green-400" />
              <p>{jobInfo.hiringManager}</p>
            </div>
          )}
          {jobInfo.companyName && (
            <div className="flex items-center gap-2 text-gray-300">
              <FaBuilding className="text-blue-400" />
              <p>{jobInfo.companyName}</p>
            </div>
          )}
          {jobInfo.companyAddress && <p className="text-gray-400 pl-6">{jobInfo.companyAddress}</p>}
        </div>
      )}

      {/* Greeting */}
      <div className="mb-8">
        <p className={`${styles.text} text-lg`}>Dear {jobInfo.hiringManager || 'Hiring Manager'},</p>
      </div>

      {/* Content */}
      <div className="space-y-6 mb-8">
        {content.introduction && (
          <div className={`${styles.section} p-6 rounded-xl`}>
            <p className={`${styles.text} whitespace-pre-line leading-relaxed`}>{content.introduction}</p>
          </div>
        )}
        {content.body && (
          <div className={`${styles.section} p-6 rounded-xl`}>
            <p className={`${styles.text} whitespace-pre-line leading-relaxed`}>{content.body}</p>
          </div>
        )}
        {content.conclusion && (
          <div className={`${styles.section} p-6 rounded-xl`}>
            <p className={`${styles.text} whitespace-pre-line leading-relaxed`}>{content.conclusion}</p>
          </div>
        )}
      </div>

      {/* Signature */}
      <div className="mt-12">
        <p className={`${styles.text} mb-4 italic`}>{content.signature || `Sincerely,\n${personalInfo.fullName}`}</p>
      </div>
    </div>
  );
};

export default CoverLetterPreview;
